import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { APPLICATION_EVENT_TYPES, EVENT_TYPES } from 'src/interfaces/enums';
import ApplicationService from 'src/services/ApplicationService';
import { AvailableCsatEvent, CsatActionTypes } from 'src/store/reducers/csat';
import { SELECTOR_CSAT_IS_LOADED } from 'src/store/reducers/csat/csat.selectors';
import useApplicationData from 'src/hooks/useApplicationData';

type EventType = EVENT_TYPES | APPLICATION_EVENT_TYPES;

export const useCsatCreateEvent = () => {
  const { csat, player } = ApplicationService;
  const { customerFeedbackEventsData, flags, applicationKey } = useApplicationData();
  const isLoaded = useSelector(SELECTOR_CSAT_IS_LOADED);
  const dispatch = useDispatch();

  const checkAvailabilityFromCsatByEvent = useCallback(
    (eventType: string) =>
      customerFeedbackEventsData?.[eventType].active &&
      (EVENT_TYPES[eventType] || APPLICATION_EVENT_TYPES[eventType]) &&
      csat?.getCustomerFeedbackGateway().canMakeFormByIdAvailable(customerFeedbackEventsData[eventType].id),
    [customerFeedbackEventsData, csat],
  );

  const dispatchFormCsatByFormIdAndEvent = useCallback(
    (id: string, eventType: EventType) => {
      csat?.getCustomerFeedbackGateway().showFeedbackFormById(id);
      dispatch({ type: CsatActionTypes.REMOVE_CSAT_AVAILABLE_EVENT, payload: eventType });
    },
    [dispatch, csat],
  );

  useEffect(() => {
    if (!flags?.ENABLE_PLAYER_CUSTOMER_FEEDBACK || isLoaded) {
      return;
    }

    csat?.getCustomerFeedbackGateway().onLoaded(
      () => {
        dispatch({ type: CsatActionTypes.SET_CSAT_AS_LOADED });
      },
      { applicationKey },
    );
  }, [isLoaded, flags, applicationKey, dispatch, csat]);

  useEffect(() => {
    const csatsEventsToCleanup: Function[] = [];

    if (!isLoaded || !customerFeedbackEventsData) {
      return;
    }

    const availableCsatsItems: AvailableCsatEvent = {} as AvailableCsatEvent;

    Object.keys(customerFeedbackEventsData)
      .filter((eventType) => checkAvailabilityFromCsatByEvent(eventType))
      .forEach((eventType) => {
        availableCsatsItems[`${eventType}`] = true;
        const callback = () =>
          dispatchFormCsatByFormIdAndEvent(customerFeedbackEventsData[eventType].id, eventType as EventType);

        let eventListener: Window | HTMLElement = window;

        if (EVENT_TYPES[eventType]) {
          eventListener = player as unknown as HTMLElement;
        }

        eventListener?.addEventListener(eventType, callback);
        csatsEventsToCleanup.push(() => eventListener?.removeEventListener?.(eventType, callback));
      });

    dispatch({ type: CsatActionTypes.SET_CSAT_AVAILABLE_EVENTS, payload: availableCsatsItems });

    return () => {
      csatsEventsToCleanup.forEach((csatEventToCleanup) => csatEventToCleanup());
    };
  }, [
    isLoaded,
    checkAvailabilityFromCsatByEvent,
    customerFeedbackEventsData,
    dispatch,
    dispatchFormCsatByFormIdAndEvent,
    player,
  ]);
};
